/**
 * Copy of the /ksiegowosc-ai landing page (www.bookeper.pl/ksiegowosc-ai): the single source for
 * everything a reader, a crawler or an AI agent gets from that page.
 *
 * Three consumers read this file and nothing else, so they cannot say different things:
 * - apps/landing renders the page, its static HTML entry and its FAQPage JSON-LD from it;
 * - apps/frontend renders the page's Markdown twin (`/ksiegowosc-ai.md`) and its llms-full.txt
 *   section from it;
 * - the tests compare the visible FAQ, the JSON-LD and the Markdown twin against it.
 *
 * Plain data on purpose: no React, no DOM, no figures about accuracy. The "Pomiar dokładności"
 * module is NOT here: it ships off, behind `ksiegowosc_ai_accuracy_module`, and its copy lives in
 * apps/landing so that no twin or llms file can leak it before `/skutecznosc` is published.
 */

export const KSIEGOWOSC_AI_PATH = '/ksiegowosc-ai';
export const KSIEGOWOSC_AI_URL = `https://www.bookeper.pl${KSIEGOWOSC_AI_PATH}`;
/** ISO date the page copy was last reviewed; the visible "Stan na" line and `dateModified` both read it. */
export const KSIEGOWOSC_AI_DATE_MODIFIED = '2026-10-28';

/** Owner-approved price line (open beta, then 50 zł a month, net). Never reworded, never given a second price. */
export const KSIEGOWOSC_AI_PRICE_LINE = 'Otwarta beta, bez karty: 0 zł · potem 50 zł/mies netto';

export const KSIEGOWOSC_AI_CTA_LABEL = 'Załóż konto: 0 zł w otwartej becie';
export const KSIEGOWOSC_AI_UTM_CAMPAIGN = 'ksiegowosc-ai';

export const KSIEGOWOSC_AI_SEO = {
  /** 49 characters (limit 60). */
  title: 'Księgowość AI dla JDG: co robi AI, a co liczy kod',
  /** At most 160 characters. */
  description: `Księgowość AI dla JDG: AI czyta dokumenty, silnik liczy z przepisów, Ty zatwierdzasz każdą deklarację. ${KSIEGOWOSC_AI_PRICE_LINE}`,
  breadcrumbName: 'Księgowość AI dla JDG',
} as const;

export interface KsiegowoscAiTable {
  caption: string;
  headers: readonly string[];
  rows: readonly (readonly string[])[];
}

export interface KsiegowoscAiLink {
  href: string;
  label: string;
}

export interface KsiegowoscAiSection {
  /** Anchor id of the H2, e.g. `#kto-odpowiada`. */
  id: string;
  heading: string;
  paragraphs: readonly string[];
  /** Shown after the first paragraph, before `closing`. */
  bullets?: readonly string[];
  /** Numbered steps, rendered as an ordered list. */
  steps?: readonly string[];
  closing?: readonly string[];
  table?: KsiegowoscAiTable;
  links?: readonly KsiegowoscAiLink[];
}

export interface KsiegowoscAiFaqItem {
  q: string;
  a: string;
}

export const KSIEGOWOSC_AI_PREPARE_ONLY =
  'Przygotowujemy deklaracje na podstawie Twoich danych. Każdą sprawdzasz i zatwierdzasz przed złożeniem; obsługa kolejnych formularzy jest rozwijana. Bookeper nie zastępuje doradcy podatkowego.';

export const KSIEGOWOSC_AI_HERO = {
  h1: 'Księgowość AI dla JDG: AI rozmawia, silnik liczy, Ty zatwierdzasz',
  /** The quotable answer: two sentences that stand alone. */
  lead: 'Księgowość AI to program, w którym sztuczna inteligencja czyta dokumenty i odpowiada na pytania, a podatki i składki liczy zwykły, powtarzalny kod oparty na przepisach. W Bookeper każdą deklarację sprawdzasz i zatwierdzasz Ty, zanim zostanie złożona.',
  productLimit: KSIEGOWOSC_AI_PREPARE_ONLY,
  secondaryLink: {
    href: '/kalkulatory/ryczalt',
    label: 'Najpierw sprawdź liczby: kalkulator ryczałtu',
  },
} as const;

export const KSIEGOWOSC_AI_SECTIONS: readonly KsiegowoscAiSection[] = [
  {
    id: 'czym-jest-ksiegowosc-ai',
    heading: 'Księgowość AI: co to znaczy w praktyce',
    paragraphs: [
      '„Księgowość AI” w wielu miejscach znaczy tylko czat dopięty do programu. U nas znaczy podział pracy:',
    ],
    bullets: [
      'AI robi to, co jest nieuporządkowane: czyta fakturę lub wyciąg, rozumie pytanie, tłumaczy przepis.',
      'Silnik liczący robi to, co ma jeden poprawny wynik: ryczałt, skalę, podatek liniowy, składki ZUS i zdrowotną. Te same dane dają ten sam wynik, a wzór ma cytat przepisu.',
      'Ty decydujesz: nic nie jest składane bez Twojego zatwierdzenia.',
    ],
    closing: [
      'Dlatego liczby nie pochodzą z „zgadywania modelu”. Mogą się mylić dane wejściowe albo interpretacja, więc przy każdej kwocie widzisz, z czego wynika i jaki przepis ją opisuje.',
    ],
    table: {
      caption: 'Podział pracy w Bookeper: kto co robi i co widzisz',
      headers: ['Zadanie', 'Kto to robi', 'Co widzisz'],
      rows: [
        ['Odczyt faktur, wyciągów, PDF-ów', 'AI', 'dane do sprawdzenia przed użyciem'],
        [
          'Odpowiedź na pytanie podatkowe',
          'AI + baza interpretacji KIS',
          'odpowiedź z linkiem do źródła',
        ],
        [
          'Liczenie ryczałtu, skali, podatku liniowego, IP Box',
          'silnik liczący',
          'wynik z cytatem przepisu',
        ],
        ['Składki ZUS i składka zdrowotna', 'silnik liczący', 'rozpiska i przepis'],
        ['Przygotowanie JPK_V7M, PIT, ZUS DRA', 'silnik liczący', 'projekt do zatwierdzenia'],
        ['Zatwierdzenie i złożenie', 'Ty', 'pełna kontrola przed złożeniem'],
      ],
    },
  },
  {
    id: 'kto-odpowiada',
    heading: 'Kto odpowiada, gdy coś pójdzie źle',
    paragraphs: [
      'Przed urzędem skarbowym i ZUS odpowiada podatnik, czyli Ty. Dotyczy to każdego modelu: czy liczy człowiek, biuro, czy program.',
      'Dlatego Bookeper przygotowuje deklaracje, a Ty je zatwierdzasz. Nie zastępujemy doradcy podatkowego. Jeśli Twoja sytuacja jest nietypowa (spór z urzędem, nietypowa struktura, pracownicy), porozmawiaj z doradcą, a Bookeper zostaw do powtarzalnej części.',
    ],
  },
  {
    id: 'sprawdz-silnik',
    heading: 'Sprawdź silnik w 30 sekund, bez konta',
    paragraphs: [
      'Kalkulatory Bookeper są publiczne i działają bez logowania. To ten sam silnik, który liczy w aplikacji. Wpisz własne dane i porównaj wynik z tym, co masz dziś.',
    ],
    links: [
      { href: '/kalkulatory/ryczalt', label: 'Kalkulator ryczałtu' },
      { href: '/kalkulatory/zus-dra', label: 'Kalkulator ZUS DRA' },
      { href: '/kalkulatory/forma-opodatkowania', label: 'Jaka forma opodatkowania' },
      { href: '/kalkulatory/skladka-zdrowotna', label: 'Składka zdrowotna' },
      { href: '/kalkulatory/b2b', label: 'B2B: ile zostaje na rękę' },
    ],
    closing: [
      'Odpowiedzi na pytania podatkowe opieramy na bazie interpretacji KIS z linkami do źródeł.',
    ],
  },
  {
    id: 'jak-zaczac',
    heading: 'Jak zacząć',
    paragraphs: [],
    steps: [
      'Zakładasz konto i podajesz NIP. Dane firmy pobieramy z rejestrów.',
      'Dodajesz dokumenty, które masz: faktury z KSeF, wyciągi, PDF-y.',
      'Sprawdzasz zaksięgowane dane i obliczenia.',
      'Zatwierdzasz deklaracje przed złożeniem.',
    ],
  },
  {
    id: 'cena',
    heading: 'Cena',
    paragraphs: [
      `${KSIEGOWOSC_AI_PRICE_LINE}. Uprzedzimy z wyprzedzeniem, zanim zaczniemy naliczać opłatę. Szczegóły planów są na stronie cennika.`,
    ],
    links: [{ href: '/#pricing', label: 'Zobacz cennik' }],
  },
];

/** The FAQ: shown on the page and, character for character, in the FAQPage JSON-LD. */
export const KSIEGOWOSC_AI_FAQ: readonly KsiegowoscAiFaqItem[] = [
  {
    q: 'Czym jest księgowość AI?',
    a: 'Księgowość AI to program, w którym sztuczna inteligencja czyta dokumenty i odpowiada na pytania, a podatki i składki liczy powtarzalny kod oparty na przepisach. W Bookeper każdą deklarację sprawdzasz i zatwierdzasz Ty, zanim zostanie złożona.',
  },
  {
    q: 'Czy AI naprawdę liczy moje podatki?',
    a: 'Nie sam model językowy. Ryczałt, skalę, podatek liniowy i składki liczy silnik, który przy tych samych danych zawsze daje ten sam wynik, a wzory mają cytaty przepisów. AI czyta dokumenty i tłumaczy, skąd wynika wynik. Dane wejściowe nadal możesz sprawdzić i poprawić.',
  },
  {
    q: 'Czy AI zastąpi księgową?',
    a: 'W powtarzalnej części prostej jednoosobowej działalności może przejąć dużo pracy: ewidencję, liczenie i przygotowanie deklaracji. Bookeper nie zastępuje doradcy podatkowego. Przy sporze z urzędem, nietypowej strukturze lub trudnej interpretacji porozmawiaj z doradcą.',
  },
  {
    q: 'Kto odpowiada za błędy w rozliczeniu?',
    a: 'Przed urzędem skarbowym i ZUS odpowiada podatnik, niezależnie od tego, czy liczył człowiek, biuro czy program. Dlatego w Bookeper przygotowujemy deklaracje, a Ty każdą sprawdzasz i zatwierdzasz przed złożeniem.',
  },
  {
    q: 'Czy Bookeper składa deklaracje za mnie?',
    a: 'Przygotowujemy deklaracje na podstawie Twoich danych. Każdą sprawdzasz i zatwierdzasz przed złożeniem. Obsługa kolejnych formularzy jest rozwijana.',
  },
  {
    q: 'Dla kogo jest Bookeper?',
    a: 'Dla jednoosobowych działalności rozliczających się ryczałtem, podatkiem liniowym albo na skali podatkowej. Jeśli masz nietypową sytuację, sprawdź najpierw kalkulatory albo skonsultuj się z doradcą podatkowym.',
  },
  {
    q: 'Ile kosztuje Bookeper?',
    a: 'Otwarta beta, bez karty: 0 zł. Potem 50 zł miesięcznie netto. Szczegóły planów są na stronie cennika.',
  },
  {
    q: 'Czy mogę sprawdzić liczby bez zakładania konta?',
    a: 'Tak. Kalkulatory ryczałtu, ZUS DRA, formy opodatkowania, składki zdrowotnej i B2B są publiczne i działają bez logowania. To ten sam silnik, który liczy w aplikacji.',
  },
  {
    q: 'Czy beta oznacza, że Bookeperowi nie można ufać?',
    a: 'Beta oznacza, że rozwijamy produkt, więc nie składamy nic bez Twojego zatwierdzenia i pokazujemy przepis przy każdej liczbie. Sprawdzaj wyniki, zwłaszcza w nietypowych sprawach.',
  },
];

export const KSIEGOWOSC_AI_FINAL_CTA = {
  heading: 'Zacznij od własnych liczb',
  body: 'Załóż konto w otwartej becie i sprawdź, co Bookeper przygotuje z Twoich danych. Każdą deklarację zatwierdzasz sam.',
  microcopy: 'Bookeper nie zastępuje doradcy podatkowego.',
} as const;

/** Links in the body of the page to other Bookeper pages (INTERNAL-LINKS.md, table A). */
export const KSIEGOWOSC_AI_RELATED_LINKS: readonly KsiegowoscAiLink[] = [
  { href: '/jdg', label: 'Bookeper dla JDG' },
  { href: '/ksef-automatycznie', label: 'automatyczna obsługa KSeF' },
  {
    href: '/blog/pillar/narzedzia-ksiegowe-jdg',
    label: 'porównanie programów księgowych dla JDG',
  },
  {
    href: '/blog/programy-do-ksiegowosci-ai-2026',
    label: 'programy do księgowości AI: porównanie 2026',
  },
  { href: '/przewodniki/ksiegowosc-online', label: 'księgowość online: ile to kosztuje' },
];

/** Footer line "Stan na 28 października 2026." (Polish, from the ISO date, locale-independent). */
export function ksiegowoscAiStateAsOf(isoDate: string = KSIEGOWOSC_AI_DATE_MODIFIED): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) throw new Error(`ksiegowosc-ai: invalid date "${isoDate}"`);
  const months = [
    'stycznia',
    'lutego',
    'marca',
    'kwietnia',
    'maja',
    'czerwca',
    'lipca',
    'sierpnia',
    'września',
    'października',
    'listopada',
    'grudnia',
  ];
  const month = months[Number(match[2]) - 1];
  if (!month) throw new Error(`ksiegowosc-ai: invalid month in "${isoDate}"`);
  return `Stan na ${Number(match[3])} ${month} ${match[1]}.`;
}

/** The page as Markdown: the twin served at `/ksiegowosc-ai.md` and the llms-full.txt document. */
export function renderKsiegowoscAiMarkdown(): string {
  const cell = (value: string) => value.replace(/\|/g, '\\|');
  const parts: string[] = [`# ${KSIEGOWOSC_AI_HERO.h1}`, KSIEGOWOSC_AI_HERO.lead];
  parts.push(KSIEGOWOSC_AI_HERO.productLimit);
  parts.push(
    `[${KSIEGOWOSC_AI_CTA_LABEL}](https://app.bookeper.pl/auth/sign-up?entity_type=jdg&utm_source=site&utm_medium=cta&utm_campaign=${KSIEGOWOSC_AI_UTM_CAMPAIGN}&utm_content=hero)\n\n${KSIEGOWOSC_AI_PRICE_LINE}`
  );
  for (const section of KSIEGOWOSC_AI_SECTIONS) {
    parts.push(`## ${section.heading}`);
    parts.push(...section.paragraphs);
    if (section.bullets) parts.push(section.bullets.map((item) => `- ${item}`).join('\n'));
    if (section.steps) parts.push(section.steps.map((item, i) => `${i + 1}. ${item}`).join('\n'));
    if (section.table) {
      const { caption, headers, rows } = section.table;
      // The page shows the caption as a line above the table (ComparisonTable); so does the twin.
      parts.push(caption);
      parts.push(
        [
          `| ${headers.map(cell).join(' | ')} |`,
          `| ${headers.map(() => '---').join(' | ')} |`,
          ...rows.map((row) => `| ${row.map(cell).join(' | ')} |`),
        ].join('\n')
      );
    }
    if (section.links) {
      parts.push(
        section.links
          .map((link) => `- [${link.label}](https://www.bookeper.pl${link.href})`)
          .join('\n')
      );
    }
    if (section.closing) parts.push(...section.closing);
  }
  parts.push('## Najczęstsze pytania');
  for (const item of KSIEGOWOSC_AI_FAQ) parts.push(`### ${item.q}\n\n${item.a}`);
  parts.push(`## ${KSIEGOWOSC_AI_FINAL_CTA.heading}`, KSIEGOWOSC_AI_FINAL_CTA.body);
  parts.push(
    `[${KSIEGOWOSC_AI_CTA_LABEL}](https://app.bookeper.pl/auth/sign-up?entity_type=jdg&utm_source=site&utm_medium=cta&utm_campaign=${KSIEGOWOSC_AI_UTM_CAMPAIGN}&utm_content=footer)\n\n${KSIEGOWOSC_AI_FINAL_CTA.microcopy}`
  );
  parts.push('## Powiązane strony');
  parts.push(
    KSIEGOWOSC_AI_RELATED_LINKS.map(
      (link) => `- [${link.label}](https://www.bookeper.pl${link.href})`
    ).join('\n')
  );
  parts.push(`---\n\n${ksiegowoscAiStateAsOf()}\n\nStrona: ${KSIEGOWOSC_AI_URL}`);
  return `${parts.join('\n\n')}\n`;
}
